import { Regex } from "@ikhokha/sales-types/common"
import { z } from "zod"

export enum SalesChannel {
	SelfService = "self-service",
	FieldSales = "field-sales",
	TeleSales = "tele-sales",
	IkStores = "ik-stores",
}

export enum SalesFulfilmentMode {
	/**
	 * The purchasing customer can get the goods themselves.
	 * @type {SalesFulfilmentMode.SelfPickup}
	 */
	SelfPickup = "self-pickup",
	/**
	 * The purchased goods need to be
	 * @type {SalesFulfilmentMode.Delivery}
	 */
	Delivery = "delivery",
}

export enum SalesDeliveryService {
	Standard = "standard",
}

export enum SalesDiscountType {
	Total = "total",
	Product = "product",
	Category = "category",
	Delivery = "shipment",
}

export const SalesPhoneNumberSchema = z
	.string()
	.min(10)
	.regex(Regex.Phone, Regex.Phone + "")

export const SalesPostalCodeSchema = z.string().regex(Regex.PostalCode, Regex.PostalCode + "")

export const SalesEmailSchema = z.string().email()

export const SalesFirstNameSchema = z
	.string()
	.min(1)
	.max(100)
	.regex(Regex.NoSpecialCharacters, Regex.NoSpecialCharacters + "")

export const SalesLastNameSchema = z
	.string()
	.min(1)
	.max(100)
	.regex(Regex.NoSpecialCharacters, Regex.NoSpecialCharacters + "")

/**
 * ISO 8601 (no offsets allowed)
 */
export const SalesDateTimeSchema = z.string().datetime()

export const SalesAddressLineSchema = z
	.string()
	.min(1)
	.max(150)
	.regex(Regex.NoSpecialCharacters, Regex.NoSpecialCharacters + "")

export const SalesAddressLine2Schema = z
	.string()
	.min(0)
	.max(150)
	.regex(Regex.NoSpecialCharacters, Regex.NoSpecialCharacters + "")

export const SalesAddressCity = z
	.string()
	.min(1)
	.max(100)
	.regex(Regex.NoSpecialCharacters, Regex.NoSpecialCharacters + "")

export const SalesSkuSchema = z
	.string()
	.min(1)
	.regex(Regex.Alphanumeric, Regex.Alphanumeric + "")

export const SalesDiscountCodeSchema = z
	.string()
	.min(1)
	.regex(Regex.Alphanumeric, Regex.Alphanumeric + "")

export const SalesPaymentRefSchema = z
	.string()
	.min(8)
	.max(8)
	.regex(Regex.Alphanumeric, Regex.Alphanumeric + "")
export type SalesPaymentRef = z.infer<typeof SalesPaymentRefSchema>

export const SalesSerialNumberSchema = z
	.string()
	.min(1)
	.max(100)
	.regex(Regex.Alphanumeric, Regex.Alphanumeric + "")

export const SalesSerialNumberMapSchema = z.object({
	sku: z
		.string()
		.min(1)
		.regex(Regex.Alphanumeric, Regex.Alphanumeric + ""),
	serial_numbers: z.array(SalesSerialNumberSchema),
})
export type SalesSerialNumberMap = z.infer<typeof SalesSerialNumberMapSchema>

export const SalesSerialNumberMapListSchema = z.array(SalesSerialNumberMapSchema)
export type SalesSerialNumberMapList = z.infer<typeof SalesSerialNumberMapListSchema>

export const SalesSerialNumberListSchema = z.array(SalesSerialNumberSchema)
export type SalesSerialNumberList = z.infer<typeof SalesSerialNumberListSchema>

export const SalesAgentSchema = z.object({
	/**
	 * Identifier of internal or external agent.
	 */
	id: z.string().min(1).max(100),
	name: SalesFirstNameSchema.nullable(),
})

export const SalesDeliveryServiceSchema = z.enum([SalesDeliveryService.Standard])

export const SalesDiscountTypeSchema = z.enum([
	SalesDiscountType.Total,
	SalesDiscountType.Product,
	SalesDiscountType.Category,
	SalesDiscountType.Delivery,
])

export enum SalesOrderStatus {
	/**
	 * Right after an order is created, it awaits payments.
	 * @type {SalesOrderStatus.PaymentPending}
	 */
	PaymentPending = "Payment_Pending",

	/**
	 * No outstanding amount left.
	 * @type {SalesOrderStatus.Paid}
	 */
	Paid = "Paid",

	/**
	 * The order is with the courier provider.
	 * (Only relevant when `fulfilment_mode=delivery`)
	 *
	 * @type {SalesOrderStatus.Shipped}
	 */
	Shipped = "Shipped",

	/**
	 * The order was shipped and is with the customer.
	 * (Only relevant when `fulfilment_mode=delivery`)
	 *
	 * @type {SalesOrderStatus.Delivered}
	 */
	Delivered = "Delivered",
}
export const SalesOrderStatusSchema = z.enum([
	SalesOrderStatus.PaymentPending,
	SalesOrderStatus.Paid,
	SalesOrderStatus.Shipped,
	SalesOrderStatus.Delivered,
])
